<mat-card id="message-container">
  <!-- Header -->
  <mat-card>
    <h1 id="title" style="text-align: center;"><b>AI Q&A</b></h1>
  </mat-card>

  <!-- Instruction Section -->
  <mat-card style="margin-top: 10px; background-color: #f9f9f9; padding: 15px; border-radius: 8px;">
    <p style="font-size: 16px; margin-bottom: 15px; text-align: center; color: #333; line-height: 1.6;">
      <strong style="font-size: 18px; color: #2c3e50;">
        Ask any question about protein interactions
      </strong><br/>
      Your question will be automatically mapped to one of our optimized AI prompts for a precise answer.
      <button mat-button color="primary" (click)="togglePredefinedQuestions()">
        {{ showPredefinedQuestions ? 'Hide' : 'Show' }} Predefined Questions
      </button>
    </p>
    <!-- Warning Message -->
    <div style="margin-top: 10px; padding: 10px; background-color: #ffebee; border-left: 4px solid #f44336; border-radius: 4px;">
      <p style="color: #d32f2f; margin: 0; font-size: 14px; text-align: center;">
        <mat-icon style="vertical-align: middle; margin-right: 5px; font-size: 18px; height: 18px; width: 18px;">warning</mat-icon>
        <strong>Warning:</strong> Do not fully rely on AI output. AI responses may contain errors or hallucinations. Always verify important information with scientific literature.
      </p>
    </div>
  </mat-card>

  <!-- Predefined Questions Section (Collapsible) -->
  <mat-card *ngIf="showPredefinedQuestions" style="margin-top: 10px; background-color: #f0f8ff; padding: 15px; border-radius: 8px;">
    <h3 style="margin-top: 0; color: #2c3e50;">Predefined Questions</h3>
    <mat-list role="list">
      <mat-list-item role="listitem" *ngFor="let question of predefinedQuestions; let i = index" style="height: auto; margin-bottom: 8px;">
        <div style="display: flex; align-items: center;">
          <span class="question-number" style="min-width: 24px; height: 24px; border-radius: 50%; background-color: #359705; color: white; display: flex; align-items: center; justify-content: center; margin-right: 10px;">{{i+1}}</span>
          <span>{{question}}</span>
        </div>
      </mat-list-item>
    </mat-list>
  </mat-card>

  <!-- Demo Example Toggle (Moved above the input box) -->
  <mat-card style="margin-top: 10px; background-color: #f0f7ff;">
    <mat-card-header>
      <mat-card-title style="display: flex; align-items: center;">
        <mat-icon style="margin-right: 8px; color: #1565c0;">lightbulb</mat-icon>
        Example
      </mat-card-title>
    </mat-card-header>
    
    <mat-card-content>
      <div style="display: flex; align-items: center; margin-bottom: 10px;">
        <strong style="margin-right: 10px;">Sample Question:</strong>
        <span style="font-style: italic;">{{demoQuestion}}</span>
      </div>
      
      <!-- Demo Results -->
      <div style="margin-top: 15px; padding: 10px; background-color: #f5f5f5; border-radius: 4px;">
        <p><strong>Mapped to:</strong></p>
        <p style="font-style: italic; color: #359705;">"{{demoMappedQuestion}}"</p>
      </div>
      
      <div style="margin-top: 15px;">
        <mat-divider></mat-divider>
        <h3 style="margin-top: 15px;">Answer:</h3>
        <div class="answer-content" style="padding: 15px; background-color: #fff; border-left: 4px solid #359705; margin-top: 10px; white-space: pre-wrap;">
          {{demoAnswer}}
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Free-style Question Input -->
  <mat-card style="margin-top: 10px;">
    <mat-form-field style="width: 100%;" appearance="outline">
      <mat-label>Enter your question</mat-label>
      <textarea 
        matInput 
        [(ngModel)]="userQuestion" 
        placeholder="Example: Can Calcium-dependent protein kinases such as CPK27 directly interact with and phosphorylated tonoplast sugar transporter 2?" 
        rows="3"
        style="resize: none;"></textarea>
    </mat-form-field>
    
    <!-- Main Action Buttons -->
    <div style="display: flex; gap: 10px;">
      <button
        mat-raised-button
        color="primary"
        (click)="onSubmit()"
        [disabled]="isLoading"
        style="flex: 3;"
      >
        <mat-icon *ngIf="!isLoading">send</mat-icon>
        <mat-spinner *ngIf="isLoading" diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
        {{ isLoading ? 'Processing...' : 'Submit' }}
      </button>
      
      <button
        mat-button
        color="warn"
        (click)="resetForm()"
        [disabled]="isLoading"
        style="flex: 1;"
      >
        <mat-icon>refresh</mat-icon> Reset
      </button>
    </div>
    
    <!-- Debug Tools -->
    <div style="display: flex; justify-content: flex-end; margin-top: 10px;">
      <button
        mat-icon-button
        color="accent"
        (click)="testApiDirectly()"
        [disabled]="isLoading"
        matTooltip="Test API directly with demo question"
      >
        <mat-icon>bug_report</mat-icon>
      </button>
    </div>
  </mat-card>

  <!-- Mapped Question Card - Shows the results from the first API call -->
  <mat-card *ngIf="!mappingInProgress && mappedQuestion" style="margin-top: 10px; background-color: #f0f7ff;">
    <mat-card-header>
      <mat-card-title style="display: flex; align-items: center;">
        <mat-icon style="margin-right: 8px; color: #1565c0;">psychology</mat-icon>
        Question Mapping
      </mat-card-title>
    </mat-card-header>
    
    <mat-card-content style="padding: 15px;">
      <div>
        <p><strong>Your question has been mapped to:</strong></p>
        <p style="font-style: italic; color: #359705; margin-bottom: 15px;">"{{mappedQuestion}}"</p>
      </div>
      
      <!-- Extracted Entities Section -->
      <div *ngIf="mappedQuestion && mappedQuestion !== predefinedQuestions[5]" style="margin-top: 15px; padding: 15px; background-color: #f5f5f5; border-radius: 4px;">
        <h4>Extracted Entities</h4>
        <p style="font-size: 0.9em; color: #666;">The following entities were extracted from your question:</p>
        
        <div style="display: flex; flex-wrap: wrap; gap: 15px;">
          <!-- Species Display -->
          <div style="flex: 1; min-width: 200px;">
            <p><strong>Species:</strong></p>
            <p style="padding: 8px; background-color: #e8f5e9; border-radius: 4px;">{{userSpecies || 'None detected'}}</p>
          </div>
          
          <!-- Protein 1 Display -->
          <div style="flex: 1; min-width: 200px;">
            <p><strong>Protein 1:</strong></p>
            <p style="padding: 8px; background-color: #e8f5e9; border-radius: 4px;">
              <ng-container *ngIf="userProtein1 && userProtein1.trim() !== ''">
                <a [href]="getGeneSymbolLink(userProtein1)" target="_blank" class="gene-link">
                  {{userProtein1}}
                  <mat-icon style="font-size: 16px; height: 16px; width: 16px; vertical-align: middle; margin-left: 4px;">open_in_new</mat-icon>
                </a>
              </ng-container>
              <ng-container *ngIf="!userProtein1 || userProtein1.trim() === ''">None detected</ng-container>
            </p>
          </div>
          
          <!-- Protein 2 Display -->
          <div style="flex: 1; min-width: 200px;">
            <p><strong>Protein 2:</strong></p>
            <p style="padding: 8px; background-color: #e8f5e9; border-radius: 4px;">
              <ng-container *ngIf="userProtein2 && userProtein2.trim() !== ''">
                <a [href]="getGeneSymbolLink(userProtein2)" target="_blank" class="gene-link">
                  {{userProtein2}}
                  <mat-icon style="font-size: 16px; height: 16px; width: 16px; vertical-align: middle; margin-left: 4px;">open_in_new</mat-icon>
                </a>
              </ng-container>
              <ng-container *ngIf="!userProtein2 || userProtein2.trim() === ''">None detected</ng-container>
            </p>
          </div>
        </div>
        
        <!-- Entity Modification Section -->
        <div style="margin-top: 15px;">
          <h4>Modify Parameters</h4>
          <p style="font-size: 0.9em; color: #666;">You can adjust these values if needed:</p>
          
          <div style="display: flex; flex-wrap: wrap; gap: 15px;">
            <!-- Species Input -->
            <mat-form-field appearance="outline" style="flex: 1; min-width: 200px;">
              <mat-label>Species</mat-label>
              <input matInput [(ngModel)]="userSpecies" placeholder="e.g., human, arabidopsis">
            </mat-form-field>
            
            <!-- Protein 1 Input -->
            <mat-form-field appearance="outline" style="flex: 1; min-width: 200px;">
              <mat-label>Protein 1</mat-label>
              <input matInput [(ngModel)]="userProtein1" placeholder="e.g., MAPK1, CPK1">
            </mat-form-field>
            
            <!-- Protein 2 Input -->
            <mat-form-field appearance="outline" style="flex: 1; min-width: 200px;">
              <mat-label>Protein 2</mat-label>
              <input matInput [(ngModel)]="userProtein2" placeholder="e.g., TP53, CDPK">
            </mat-form-field>
          </div>
          
          <button mat-raised-button color="primary" (click)="updateWithUserEntities()" style="margin-top: 10px;">
            <mat-icon>refresh</mat-icon> Update Answer
          </button>
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Results Section - Always Visible -->
  <mat-card style="margin-top: 10px;">
    <mat-card-header>
      <mat-card-title>Results</mat-card-title>
    </mat-card-header>
    
    <mat-card-content>
      <!-- Initial State (No Question Submitted Yet) -->
      <div *ngIf="!isLoading && !mappingInProgress && !messageContent && !mappedQuestion" style="padding: 20px; text-align: center;">
        <p style="color: #666; font-style: italic;">Enter your question above and click Submit to see results here.</p>
      </div>
      
      <!-- Mapping In Progress -->
      <div *ngIf="mappingInProgress" style="padding: 20px; text-align: center;">
        <p>Mapping your question to a predefined template...</p>
        <mat-spinner style="margin: 0 auto;" [diameter]="30"></mat-spinner>
      </div>
      
      <!-- Waiting for Answer -->
      <div *ngIf="!mappingInProgress && mappedQuestion && !messageContent && !isLoading" style="padding: 20px; text-align: center;">
        <p>Ready to generate answer...</p>
      </div>
      
      
      <!-- Answer (when available) -->
      <div *ngIf="!isLoading && messageContent" style="margin-top: 15px;">
        <mat-divider></mat-divider>
        <h3 style="margin-top: 15px;">Answer:</h3>
        <div class="answer-content" style="padding: 15px; background-color: #fff; border-left: 4px solid #359705; margin-top: 10px; white-space: pre-wrap;">
          {{messageContent}}
        </div>
      </div>
      
      <!-- Loading State for Answer -->
      <div *ngIf="isLoading && !mappingInProgress" style="text-align: center; padding: 20px;">
        <mat-spinner diameter="30" style="margin: 0 auto;"></mat-spinner>
        <p>Generating answer based on scientific literature...</p>
      </div>
    </mat-card-content>
  </mat-card>
</mat-card>
